/**
 * Disclosure legend style — the browser side of `backend/legend_style.py`.
 *
 * The real legend is drawn by Pillow at publish time (and in the
 * Settings preview, which asks the server for a render). This module
 * only keeps the article page's on-screen overlay in step with the
 * property's style, so what the editor sees over the featured image
 * matches what gets burnt in: same font, colours, opacity, corners,
 * padding, corner position, and size as a share of the image width.
 *
 * Keep DEFAULT_LEGEND_STYLE / LIMITS identical to the backend's
 * DEFAULT_STYLE / LIMITS. Unit-tested in scripts/legendStyle.test.mjs.
 */

export const DEFAULT_LEGEND_STYLE = {
  font_family: "sans",
  font_weight: "regular",
  size_pct: 1.3,
  uppercase: false,
  text_color: "#ffffff",
  text_opacity: 100,
  bg_color: "#000000",
  bg_opacity: 71,
  corner_pct: 60,
  padding: 0.5,
  position: "bottom_left",
};

export const LEGEND_POSITIONS = [
  { key: "top_left", label: "Top left" },
  { key: "top_right", label: "Top right" },
  { key: "bottom_left", label: "Bottom left" },
  { key: "bottom_right", label: "Bottom right" },
];

export const LEGEND_WEIGHTS = [
  { key: "regular", label: "Regular" },
  { key: "bold", label: "Bold" },
  { key: "italic", label: "Italic" },
  { key: "bolditalic", label: "Bold italic" },
];

export const LEGEND_LIMITS = {
  size_pct: [0.6, 4.0],
  text_opacity: [0, 100],
  bg_opacity: [0, 100],
  corner_pct: [0, 100],
  padding: [0.2, 2.0],
};

const MIN_FONT_PX = 10;
// The on-screen overlay is a scaled-down image, so allow smaller text
// than the baked floor — but keep it readable.
const MIN_SCREEN_FONT_PX = 7;

/** The saved style over the defaults. Invalid / missing keys fall back. */
export function resolveLegendStyle(stored) {
  const out = { ...DEFAULT_LEGEND_STYLE };
  if (!stored || typeof stored !== "object") return out;
  for (const k of Object.keys(DEFAULT_LEGEND_STYLE)) {
    const v = stored[k];
    if (v === undefined || v === null || v === "") continue;
    if (k in LEGEND_LIMITS) {
      const n = Number(v);
      if (!Number.isFinite(n)) continue;
      const [lo, hi] = LEGEND_LIMITS[k];
      out[k] = Math.min(hi, Math.max(lo, n));
    } else if (k === "uppercase") {
      out[k] = !!v;
    } else {
      out[k] = String(v);
    }
  }
  return out;
}

/** "#rrggbb" + opacity 0–100 → "rgba(r,g,b,a)". */
export function rgba(hex, opacityPct) {
  let s = String(hex || "").trim().replace(/^#/, "");
  if (s.length === 3) s = s.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(s)) s = "000000";
  const a = Math.max(0, Math.min(100, Number(opacityPct) || 0)) / 100;
  const r = parseInt(s.slice(0, 2), 16);
  const g = parseInt(s.slice(2, 4), 16);
  const b = parseInt(s.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${Math.round(a * 1000) / 1000})`;
}

/** Font slug ("playfair-display", "sans") → CSS font-family stack. */
export function fontStack(slug) {
  const s = String(slug || "sans");
  if (s === "sans") return "'Liberation Sans', Arial, Helvetica, sans-serif";
  if (s === "serif") return "'Liberation Serif', 'Times New Roman', serif";
  if (s === "mono") return "'Liberation Mono', ui-monospace, monospace";
  const name = s
    .split("-")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
  return `'${name}', system-ui, sans-serif`;
}

/** Font px the backend uses for an image `width` px wide. */
export function bakedFontPx(width, style) {
  const st = resolveLegendStyle(style);
  return Math.max(MIN_FONT_PX, Math.round(Math.max(1, width) * st.size_pct / 100));
}

/**
 * Inline CSS for the article page's overlay, laid over an image shown
 * `displayWidth` px wide (absolute-positioned inside a `relative` box).
 * Proportions follow the backend: font = size_pct% of width, padding =
 * padding × font, margin = 0.5 × font, radius = corner_pct% of half the
 * pill height (approximated as font × 1.2 + 2 × padding).
 */
export function legendOverlayStyle(style, displayWidth) {
  const st = resolveLegendStyle(style);
  const w = Math.max(1, Number(displayWidth) || 0);
  const font = Math.max(MIN_SCREEN_FONT_PX, (w * st.size_pct) / 100);
  const pad = font * st.padding;
  const margin = font * 0.5;
  const pillH = font * 1.2 + pad * 2;
  const [v, h] = st.position.split("_");
  const weight = st.font_weight;
  const css = {
    position: "absolute",
    [v === "top" ? "top" : "bottom"]: `${round1(margin)}px`,
    [h === "right" ? "right" : "left"]: `${round1(margin)}px`,
    padding: `${round1(pad)}px`,
    fontSize: `${round1(font)}px`,
    lineHeight: 1.2,
    fontFamily: fontStack(st.font_family),
    fontWeight: weight.startsWith("bold") ? 700 : 400,
    fontStyle: weight.endsWith("italic") ? "italic" : "normal",
    textTransform: st.uppercase ? "uppercase" : "none",
    color: rgba(st.text_color, st.text_opacity),
    backgroundColor: rgba(st.bg_color, st.bg_opacity),
    borderRadius: `${round1((pillH / 2) * (st.corner_pct / 100))}px`,
    maxWidth: `calc(100% - ${round1(margin * 2)}px)`,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
  return css;
}

function round1(n) {
  return Math.round(n * 10) / 10;
}
